<template>
  <PortalForm
    :portalType="portalType"
    :title="title"
    :headerIconSrc="homeInsuranceFormIcon"
    :steps="steps"
    :current-step="currentStep"
    :form-data="formData"
    :form-valid="isStepValid"
    :loading="loading"
    :success-title="successTitle"
    :success-message="successMessage"
    :reference-number="referenceNumber"
    :success-details="successDetails"
    :next-steps="nextSteps"
    :enable-email-receipt="true"
    :user-email="formData.email"
    :form-id="'home-policy-change'"
    @update:formData="updateFormData"
    @step-change="handleStepChange"
    @form-submit="submitForm"
    @draft-restored="handleDraftRestored"
    @back="navigateAway"
    @primary-action="returnToDashboard"
    @email-sent="handleEmailSent"
  >
    <!-- Contact Information Step -->
    <SharedContactInformationStep 
      v-if="currentStep === 1"
      v-model:firstName="formData.firstName"
      v-model:lastName="formData.lastName"
      v-model:policyNumber="formData.policyNumber"
      v-model:phoneNumber="formData.phoneNumber"
      v-model:email="formData.email"
      :customerType="portalType"
      :errors="errors"
      @next="goToNextStep"
    />
    
    <!-- Changes Selection Step -->
    <ChangesSelectionStep
      v-if="currentStep === 2"
      v-model="formData.selectedChanges"
      :customerType="portalType"
      :errors="errors"
      @previous="goToPreviousStep"
      @next="goToNextStep"
      @update:errors="handleErrors"
      ref="changesSelectionRef"
    />
    
    <!-- Document Upload Step -->
    <SharedDocumentUploadStep
      v-if="currentStep === 3"
      v-model:documents="formData.documents"
      v-model:currentDocumentType="formData.currentDocumentType"
      v-model:documentDescription="formData.documentDescription"
      :customerType="portalType"
      :errors="errors"
      :document-types="documentTypes"
      @previous="goToPreviousStep"
      @next="goToNextStep"
    />
    
    <!-- Effective Date and Review Step -->
    <EffectiveDateStep
      v-if="currentStep === 4"
      v-model:effectiveDate="formData.effectiveDate"
      v-model:comments="formData.comments"
      v-model:signature="formData.signature"
      v-model:signatureDate="formData.signatureDate"
      :customerType="portalType"
      :isSubmitting="isSubmitting"
      :errors="errors"
      @previous="goToPreviousStep"
      @submit="handleSubmit"
    />
  </PortalForm>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import PortalForm from '@/components/common/PortalForm.vue';
import SharedContactInformationStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedContactInformationStep.vue';
import ChangesSelectionStep from './steps/ChangesSelectionStep.vue';
import EffectiveDateStep from './steps/EffectiveDateStep.vue';
import SharedDocumentUploadStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedDocumentUploadStep.vue';
import { validateEmail, validatePhoneNumber, validatePolicyNumber } from '@/utils/validators';
import { validateEffectiveDate } from '@/utils/policyValidators';
import { useHomeInsuranceService } from '@/services/homeInsuranceService';
import { useRouter } from 'vue-router';
import homeInsuranceFormIcon from '@/assets/images/ui/icons/homeInsuranceChanges.svg'; // Assuming this is the icon you want
import { getTodayFormatted } from '@/utils/dateUtils';

console.log('[HomePolicyChangeForm] Debug ET Date for form load:', getTodayFormatted());

// 1. Props
const props = defineProps({
  title: {
    type: String,
    default: 'Home Policy Change Request'
  },
  portalType: {
    type: String,
    default: 'customer',
    validator: value => ['customer', 'lender'].includes(value)
  }
});

// Initial Prop Logging (Can stay here as props are defined)
console.log('[HomePolicyChangeForm] Initializing with props:', props);

// 2. Service/Router Initializations
const router = useRouter();
const homeInsuranceService = useHomeInsuranceService();

// 3. Core Reactive State Declarations
const currentStep = ref(1);
const loading = ref(false);
const isSubmitting = ref(false);
const errors = ref({});
const changesSelectionRef = ref(null); 
const referenceNumber = ref('');
const isStep2Validated = ref(false);
const isStep3Validated = ref(true);
const documentUploadRef = ref(null);

const formData = ref({
  firstName: '',
  lastName: '',
  policyNumber: '',
  phoneNumber: '',
  email: '',
  selectedChanges: {
    updateCoveragesChecked: false,
    addInsuredChecked: false,
    removeInsuredChecked: false,
    changeDetails: {
      coveragesData: {},
      addInsuredData: {},
      removeInsuredData: {}
    }
  },
  documents: [],
  currentDocumentType: '',
  documentDescription: '',
  effectiveDate: getTodayFormatted(),
  signature: '',
  signatureDate: getTodayFormatted(),
  comments: ''
});

const successTitle = ref('Request Submitted Successfully!');
const successMessage = ref('Your home policy change request has been successfully submitted for review. Our team will process your request and contact you regarding its status and confirmation from your insurance carrier.');
const successDetails = ref([]);
const nextSteps = ref([
  'A confirmation email will be sent to you shortly.',
  'An agent will review your request within 1-2 business days.',
  'If additional information is needed, we will contact you via email or phone.'
]);

// 4. Static Data (Dynamic Document Types)
const customerDocumentTypes = [
  { id: 'property_photos', label: 'Property Photos', description: 'Recent photos of your home', required: false },
  { id: 'id_verification', label: 'ID Verification', description: 'A copy of your driver\'s license or ID', required: false }, // Made optional for customer too, as per general discussion
  { id: 'proof_of_ownership', label: 'Proof of Ownership', description: 'Documents showing ownership of the property', required: false },
  { id: 'appraisal', label: 'Property Appraisal', description: 'Recent home appraisal if requesting value changes', required: false }
];

const lenderDocumentTypes = [
  { id: 'lender_authorization', label: 'Lender/Borrower Authorization', description: 'Signed authorization to make changes or request information', required: true },
  { id: 'request_letterhead', label: 'Request on Letterhead', description: 'Official request from the lending institution', required: false },
  { id: 'declaration_page', label: 'Existing Declaration Page', description: 'Copy of the current policy declaration page (if available)', required: false }
];

const documentTypes = computed(() => {
  return props.portalType === 'lender' ? lenderDocumentTypes : customerDocumentTypes;
});

// 5. Computed Properties
const steps = computed(() => [
  { label: 'Contact Information', index: 1 },
  { label: 'Request Changes', index: 2 },
  { label: 'Documents', index: 3 },
  { label: 'Review and Submit', index: 4 }
]);

const isStepValid = computed(() => {
  console.log(`[HomePolicyChangeForm] isStepValid computed for currentStep: ${currentStep.value}`);
  let validStatus;
  if (currentStep.value === 1) {
    validStatus = validateStep1(false);
  } else if (currentStep.value === 2) {
    validStatus = isStep2Validated.value;
  } else if (currentStep.value === 3) {
    validStatus = validateStep3(false);
  } else if (currentStep.value === 4) {
    validStatus = validateStep4(false);
  } else {
    validStatus = true; 
  }
  console.log(`[HomePolicyChangeForm] isStepValid result: ${validStatus}`);
  return validStatus;
});

// 6. Functions (Validation, Navigation, Data Handling, Submission, etc.)
const validateStep1 = (updateErrors = true) => {
  console.log(`[HomePolicyChangeForm] validateStep1 called. updateErrors: ${updateErrors}, Data:`, JSON.parse(JSON.stringify({
    firstName: formData.value.firstName,
    lastName: formData.value.lastName,
    policyNumber: formData.value.policyNumber,
    phoneNumber: formData.value.phoneNumber,
    email: formData.value.email,
  })));
  const tempErrors = {
    firstName: !formData.value.firstName ? 'First name is required' : '',
    lastName: !formData.value.lastName ? 'Last name is required' : '',
    policyNumber: validatePolicyNumber(formData.value.policyNumber),
    phoneNumber: validatePhoneNumber(formData.value.phoneNumber),
    email: validateEmail(formData.value.email)
  };
  const isValid = !Object.values(tempErrors).some(error => error);
  console.log(`[HomePolicyChangeForm] validateStep1 result: ${isValid}, Errors (if updateErrors):`, updateErrors ? tempErrors : 'Not updated');
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  return isValid;
};

const validateStep2 = async (updateErrors = true) => {
  console.log(`[HomePolicyChangeForm] validateStep2 called. updateErrors: ${updateErrors}, changesSelectionRef available: ${!!changesSelectionRef.value}`);
  let isChangeValid = true;
  let tempErrors = {};
  const { updateCoveragesChecked, addInsuredChecked, removeInsuredChecked } = formData.value.selectedChanges;
  const hasSelectedChanges = updateCoveragesChecked || addInsuredChecked || removeInsuredChecked;

  if (!hasSelectedChanges) {
    tempErrors.changeSelection = 'Please select at least one change';
    isChangeValid = false;
  } else {
    tempErrors.changeSelection = '';
    if (changesSelectionRef.value && typeof changesSelectionRef.value.validate === 'function') {
      console.log('[HomePolicyChangeForm] validateStep2: Calling changesSelectionRef.value.validate()');
      isChangeValid = await changesSelectionRef.value.validate();
      console.log('[HomePolicyChangeForm] validateStep2: changesSelectionRef.value.validate() result:', isChangeValid);
    } else {
      console.error("[HomePolicyChangeForm] changesSelectionRef or its validate method is not available for step 2 validation.");
      isChangeValid = false; // Default to invalid if the child component can't be validated
      tempErrors.changeSelection = 'Cannot validate change details at the moment. Child component not ready.';
    }
  }
  console.log(`[HomePolicyChangeForm] validateStep2 result: ${isChangeValid}, TempErrors:`, tempErrors);
  if (updateErrors) {
    const newErrors = { ...errors.value, ...tempErrors };
    if (isChangeValid) {
      if (newErrors.changeSelection === '') delete newErrors.changeSelection;
      if (newErrors.sectionDetailError) delete newErrors.sectionDetailError; 
    }
    errors.value = newErrors;
  } else if (!isChangeValid && tempErrors.changeSelection) {
    // If not updating main errors, but specific validation found an issue, ensure it's reflected for immediate feedback if needed
    // This might be too aggressive if updateErrors is false, consider if this is desired
    // errors.value = { ...errors.value, ...tempErrors }; 
  }
  // isStep2Validated.value = isChangeValid; // The watcher will set this based on the result
  return isChangeValid; // Return the current validation status
};

const validateStep3 = (updateErrors = true) => {
  console.log('[HomePolicyChangeForm] validateStep3 called. updateErrors:', updateErrors, 'PortalType:', props.portalType);
  console.log('[HomePolicyChangeForm] validateStep3: formData.value.documents at start:', JSON.parse(JSON.stringify(formData.value.documents)));

  // If customer, document upload is optional
  if (props.portalType === 'customer') {
    console.log('[HomePolicyChangeForm] validateStep3: Customer portal, documents optional. Validation passes.');
    isStep3Validated.value = true;
    if (updateErrors) errors.value.documents = ''; // Clear any previous document errors for customer
    return true;
  }

  // For lenders (or other types if added later), proceed with existing validation
  let tempErrors = { documents: '' };
  let isValid = true;

  if (!formData.value.documents || formData.value.documents.length === 0) {
    console.log('[HomePolicyChangeForm] validateStep3: No documents found (for non-customer).');
    tempErrors.documents = 'At least one document is required.';
    isValid = false;
  } else {
    const hasRequiredDoc = formData.value.documents.some(doc => doc.file && doc.type);
    if (!hasRequiredDoc) {
      console.log('[HomePolicyChangeForm] validateStep3: A document is present but seems invalid (missing file or type for non-customer).');
      tempErrors.documents = 'A valid document with a type and file is required.';
      isValid = false;
    }
  }

  if (updateErrors) {
    console.log('[HomePolicyChangeForm] validateStep3: Updating errors object with:', tempErrors);
    errors.value = { ...errors.value, ...tempErrors };
  } else {
    console.log('[HomePolicyChangeForm] validateStep3: Not updating errors object (updateErrors is false).');
  }
  
  isStep3Validated.value = isValid; // Update reactive state
  console.log('[HomePolicyChangeForm] validateStep3 result (for non-customer):', isValid, ', Errors (if updateErrors):', updateErrors ? tempErrors : 'Not updated');
  return isValid;
};

const validateStep4 = (updateErrors = true) => {
  console.log(`[HomePolicyChangeForm] validateStep4 called. updateErrors: ${updateErrors}, Data:`, JSON.parse(JSON.stringify({
    effectiveDate: formData.value.effectiveDate,
    signature: formData.value.signature,
    signatureDate: formData.value.signatureDate,
  })));
  const tempErrors = {
    effectiveDate: validateEffectiveDate(formData.value.effectiveDate),
    signature: !formData.value.signature ? 'Signature is required' : '',
    signatureDate: !formData.value.signatureDate ? 'Signature date is required' : ''
  };
  const isValid = !Object.values(tempErrors).some(error => error);
  console.log(`[HomePolicyChangeForm] validateStep4 result: ${isValid}, Errors (if updateErrors):`, updateErrors ? tempErrors : 'Not updated');
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  return isValid;
};

const handleStepChange = (newStep) => {
  console.log(`[HomePolicyChangeForm] handleStepChange called with newStep: ${newStep}`);
  currentStep.value = newStep;
};

const goToNextStep = async () => {
  console.log(`[HomePolicyChangeForm] goToNextStep called from currentStep: ${currentStep.value}`);
  let isValid = false;
  if (currentStep.value === 1) {
    isValid = validateStep1();
  } else if (currentStep.value === 2) {
    isValid = await validateStep2();
  } else if (currentStep.value === 3) {
    isValid = validateStep3();
  }
  
  if (isValid) {
    if (currentStep.value < steps.value.length) {
        currentStep.value++;
        console.log(`[HomePolicyChangeForm] goToNextStep: Validation passed, moved to step: ${currentStep.value}`);
    } else {
        console.log(`[HomePolicyChangeForm] goToNextStep: Validation passed, already on last step: ${currentStep.value}`);
    }
  } else {
    console.log('[HomePolicyChangeForm] goToNextStep: Validation failed, staying on step:', currentStep.value);
  }
};

const goToPreviousStep = () => {
  console.log(`[HomePolicyChangeForm] goToPreviousStep called from currentStep: ${currentStep.value}`);
  if (currentStep.value > 1) {
    currentStep.value--;
    console.log(`[HomePolicyChangeForm] goToPreviousStep: Moved to step: ${currentStep.value}`);
  }
};

const updateFormData = (newFormData) => {
  console.log('[HomePolicyChangeForm] updateFormData called with:', JSON.parse(JSON.stringify(newFormData)));
  if (JSON.stringify(formData.value) !== JSON.stringify(newFormData)) {
    formData.value = newFormData;
    console.log('[HomePolicyChangeForm] updateFormData: formData updated.');
  } else {
    console.log('[HomePolicyChangeForm] updateFormData: No change in formData, not updating.');
  }
};

const handleDraftRestored = (draft) => {
  console.log('[HomePolicyChangeForm] handleDraftRestored called with draft:', JSON.parse(JSON.stringify(draft)));
  // Example: formData.value = { ...formData.value, ...draft.formData };
  // currentStep.value = draft.currentStep || 1;
};

const handleErrors = (newErrors) => {
  console.log('[HomePolicyChangeForm] handleErrors (from child) called with newErrors:', JSON.parse(JSON.stringify(newErrors)));
  if (JSON.stringify(errors.value) !== JSON.stringify(newErrors)) {
    errors.value = { ...errors.value, ...newErrors };
    console.log('[HomePolicyChangeForm] handleErrors: errors updated from child.');
  } else {
    console.log('[HomePolicyChangeForm] handleErrors: No change in errors from child, not updating.');
  }
};

// Add this method to flatten the nested structure before submission
const flattenFormData = (data) => {
  const formData = new FormData();
  
  // Basic fields
  formData.append('firstName', data.firstName);
  formData.append('lastName', data.lastName);
  formData.append('policyNumber', data.policyNumber);
  formData.append('phoneNumber', data.phoneNumber);
  formData.append('email', data.email);
  formData.append('customerType', props.portalType);
  formData.append('effectiveDate', data.effectiveDate);
  formData.append('comments', data.comments || '');
  formData.append('signature', data.signature);
  formData.append('signatureDate', data.signatureDate);
  formData.append('message', data.message || '');
  
  // Change flags
  formData.append('updateCoveragesChecked', data.selectedChanges?.updateCoveragesChecked?.toString() || 'false');
  formData.append('addInsuredChecked', data.selectedChanges?.addInsuredChecked?.toString() || 'false');
  formData.append('removeInsuredChecked', data.selectedChanges?.removeInsuredChecked?.toString() || 'false');
  
  // Coverage data
  if (data.selectedChanges?.updateCoveragesChecked) {
    const coverageData = data.selectedChanges.changeDetails?.coveragesData || {};
    formData.append('dwellingAmount', coverageData.dwellingAmount || '');
    formData.append('personalPropertyAmount', coverageData.personalPropertyAmount || '');
    formData.append('deductibleAmount', coverageData.deductibleAmount || '');
  }
  
  // Add insured data
  if (data.selectedChanges?.addInsuredChecked) {
    const addInsuredData = data.selectedChanges.changeDetails?.addInsuredData || {};
    formData.append('insuredFirstName', addInsuredData.insuredFirstName || '');
    formData.append('insuredLastName', addInsuredData.insuredLastName || '');
  }
  
  // Remove insured data
  if (data.selectedChanges?.removeInsuredChecked) {
    const removeInsuredData = data.selectedChanges.changeDetails?.removeInsuredData || {};
    formData.append('insuredFirstNameToRemove', removeInsuredData.insuredFirstNameToRemove || '');
    formData.append('insuredLastNameToRemove', removeInsuredData.insuredLastNameToRemove || '');
  }
  
  // Documents
  if (data.documents && data.documents.length > 0) {
    const document = data.documents[0];
    if (document.file) {
      formData.append('document', document.file);
    }
  }
  
  return formData;
};

// Modify the handleSubmit method to use the flattened structure
const handleSubmit = async () => {
  if (!validateStep4(true)) {
    // Show alert if validation fails
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  
  try {
    isSubmitting.value = true;
    loading.value = true;
    
    console.log('[HomePolicyChangeForm] Submitting form data:', JSON.parse(JSON.stringify(formData.value)));
    
    // Use the flatten method to convert nested structure to flat structure
    const flattenedFormData = flattenFormData(formData.value);
    
    // Log what's being sent to the API
    console.log('[HomePolicyChangeForm] Submitting flattened form data to API');
    
    // Submit using the flattened FormData
    const response = await homeInsuranceService.submitPolicyChange(flattenedFormData);
    
    // Handle success
    console.log('[HomePolicyChangeForm] Form submission successful:', response);
    referenceNumber.value = response.ticket_id || '';
    
    // Prepare success details
    successDetails.value = [
      { label: 'Reference #', value: referenceNumber.value },
      { label: 'Policy Number', value: formData.value.policyNumber },
      { label: 'Submission Date', value: new Date().toLocaleDateString() }
    ];
    
    // Move to success state by incrementing step to steps.value.length + 1
    currentStep.value = steps.value.length + 1;
    
    // Clear form data
    resetForm();
    loading.value = false;
    isSubmitting.value = false;
    
  } catch (error) {
    console.error('[HomePolicyChangeForm] Form submission error:', error);
    loading.value = false;
    isSubmitting.value = false;
    
    // Handle validation errors
    if (error.response?.data?.errors) {
      errors.value = { ...errors.value, ...error.response.data.errors };
    } else {
      // Display general error
      errors.value = { 
        ...errors.value, 
        submission: error.message || 'An error occurred while submitting your request. Please try again later.' 
      };
    }
    
    // Scroll to top to show error
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

// Add the missing submitForm function that delegates to handleSubmit
const submitForm = async (formData) => {
  console.log('[HomePolicyChangeForm] submitForm called from PortalForm, delegating to handleSubmit');
  // Save the form data if needed
  updateFormData(formData);
  // Call the actual submission handler
  return handleSubmit();
};

// Add a resetForm function to clear form data
const resetForm = () => {
  formData.value = {
    firstName: '',
    lastName: '',
    policyNumber: '',
    phoneNumber: '',
    email: '',
    selectedChanges: {
      updateCoveragesChecked: false,
      addInsuredChecked: false,
      removeInsuredChecked: false,
      changeDetails: {
        coveragesData: {},
        addInsuredData: {},
        removeInsuredData: {}
      }
    },
    documents: [],
    currentDocumentType: '',
    documentDescription: '',
    effectiveDate: getTodayFormatted(),
    signature: '',
    signatureDate: getTodayFormatted(),
    comments: ''
  };
  errors.value = {};
  isStep2Validated.value = false;
  isStep3Validated.value = true;
  console.log('[HomePolicyChangeForm] Form data reset');
};

const handleEmailSent = (email) => {
  console.log(`[HomePolicyChangeForm] Confirmation email sent to ${email} (simulated or handled by PortalForm).`);
};

const returnToDashboard = () => {
  console.log('[HomePolicyChangeForm] returnToDashboard called.');
  router.push('/dashboard');
};

const navigateAway = () => { 
  console.log('[HomePolicyChangeForm] navigateAway (back) called.');
  if (router.options.history.state.back) {
    router.back();
  } else {
    router.push('/customerservice/policy-changes'); 
  }
};

// 7. Watchers
watch(currentStep, (newVal, oldVal) => {
  console.log(`[HomePolicyChangeForm] currentStep changed from ${oldVal} to ${newVal}.`);
});

watch(formData, (newVal) => {
  console.log('[HomePolicyChangeForm] formData changed (deep watch). New state:', JSON.parse(JSON.stringify(newVal)));
}, { deep: true });

watch(isStep2Validated, (newVal) => {
  console.log(`[HomePolicyChangeForm] isStep2Validated (for async step 2) changed to: ${newVal}`);
});

watch([currentStep, changesSelectionRef], async ([newStep, changesRefVal]) => {
  console.log(`[HomePolicyChangeForm] Watcher for step 2 validation triggered. Current step: ${newStep}, changesSelectionRef available: ${!!changesRefVal}`);
  if (newStep === 2) {
    if (changesRefVal) {
        console.log('[HomePolicyChangeForm] Watcher: Current step is 2 and changesSelectionRef exists. Re-validating step 2.');
        isStep2Validated.value = await validateStep2(false); 
        console.log(`[HomePolicyChangeForm] Watcher: Step 2 validation finished. isStep2Validated set to: ${isStep2Validated.value}`);
    } else {
        console.warn('[HomePolicyChangeForm] Watcher: Current step is 2 but changesSelectionRef is NOT YET available. Validation will be attempted by isStepValid or next action.');
        isStep2Validated.value = false; // Assume invalid until ref is ready and validated
    }
  }
}, { deep: false, immediate: false }); // Using deep:false as we only care if changesRefVal itself changes or currentStep

watch(() => formData.value.documents, (newDocs, oldDocs) => {
  console.log('[HomePolicyChangeForm] formData.value.documents changed (watcher).');
  console.log('[HomePolicyChangeForm] Old documents:', JSON.parse(JSON.stringify(oldDocs)));
  console.log('[HomePolicyChangeForm] New documents:', JSON.parse(JSON.stringify(newDocs)));
  // If documents change, re-validate step 3 if it's the current step or a future step that depends on it.
  if (currentStep.value >= 3) {
    console.log('[HomePolicyChangeForm] Documents changed, revalidating step 3 silently.');
    validateStep3(false); // Re-validate without showing errors to user immediately
  }
}, { deep: true });

// 8. Lifecycle Hooks
onMounted(() => {
  console.log('[HomePolicyChangeForm] Component mounted.');
  console.log('[HomePolicyChangeForm] Initial currentStep:', currentStep.value);
  console.log('[HomePolicyChangeForm] Initial formData:', JSON.parse(JSON.stringify(formData.value))); 
});

</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}

.input-responsive {
  @apply mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm;
}

.form-field {
  @apply mb-4;
}
</style> 